"use client";

import { createContext, useContext, useState } from "react";
import { useRouter } from "next/navigation";

export type Language = "en" | "es";

interface LanguageContextType {
  lang: Language;
  toggleLang: () => void;
  t: (en: string, es: string) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  lang: "en",
  toggleLang: () => {},
  t: (en) => en,
});

export const useLang = () => useContext(LanguageContext);

// Phase 3a (SEO): the marketing surface used to be client-only — this provider
// returned `null` until it mounted, so the server sent a blank shell and Google
// saw no content. Now the locale is resolved on the SERVER (the marketing layout
// reads the `redy-locale` cookie and passes `initialLang`), the provider renders
// its children immediately, and the marketing tree is server-rendered. The
// public API (`useLang` → `t`/`lang`/`toggleLang`) is unchanged, so the ~168
// components that consume it need no edits.
export function LanguageProvider({
  children,
  initialLang = "en",
}: {
  children: React.ReactNode;
  /** Server-resolved locale (from the `redy-locale` cookie). Defaults to EN —
      the canonical, unprefixed default language. */
  initialLang?: Language;
}) {
  const router = useRouter();
  const [lang, setLang] = useState<Language>(initialLang);

  const toggleLang = () => {
    const next = lang === "en" ? "es" : "en";
    setLang(next);
    // Persist to the same cookie the rest of the site (next-intl, /help, /blog)
    // reads, so the language choice is sticky and consistent everywhere. Then
    // refresh so any server-rendered content (e.g. blog) re-renders in the new
    // locale, not just the client-driven marketing copy.
    if (typeof document !== "undefined") {
      document.cookie = `redy-locale=${next};path=/;max-age=31536000;SameSite=Lax`;
    }
    router.refresh();
  };

  const t = (en: string, es: string) => (lang === "en" ? en : es);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
